import { LifeBuoy } from 'lucide-react';
import ServiceStoryPage from '../../components/ServiceStoryPage';

const color = 'hsl(243 75% 59%)';

export const seo = {
  title: 'Ongoing Support',
  tagline: 'Your long-term digital partner, always on hand',
  metaDesc: 'Ongoing digital support for UK small businesses — website updates, content changes, technical fixes, account management and continuous SEO & AEO.',
  slug: 'ongoing-support',
  whatWeDoItems: [
    { heading: 'Website updates', body: 'Copy changes, new pages, layout tweaks, image swaps — whatever your site needs to stay current, handled quickly without the back-and-forth.' },
    { heading: 'Design changes', body: 'Refreshing your look, updating branding, or adjusting how something appears — small changes that keep your site looking sharp.' },
    { heading: 'Content changes', body: 'New services, updated prices, seasonal offers, blog posts — we keep your content fresh so customers always see the right information.' },
    { heading: 'Technical fixes', body: 'Something not working right? We diagnose and fix issues across your site and tools fast, before they turn into real problems.' },
    { heading: 'Account & platform help', body: 'A hand managing your accounts across Google, social media, email and the other tools your business relies on day to day.' },
    { heading: 'Ongoing SEO', body: 'Continuous work on keywords, structure and authority to keep your site climbing the rankings over time, not just at launch.' },
    { heading: 'AEO improvements', body: 'Making sure your business shows up in AI-generated answers and voice search — the next place your customers will be looking.' },
    { heading: 'Extra setup work', body: 'New integrations, extra platforms, tools you want to add later — we handle the setup so your digital side keeps pace with your growth.' },
    { heading: 'Anything digital', body: 'If it’s digital and it needs doing, we’re the people to call. One team, one relationship, no drama about scope.' },
  ],
};

export default function OngoingSupportPage() {
  return (
    <ServiceStoryPage
      tagline={seo.tagline}
      Icon={LifeBuoy}
      color={color}
      ctaText="Book a free call"
      heroHeadline="Digital help whenever you need it, from a team that already knows your setup"
      heroSub="Launching is just the start. Ongoing Support keeps everything running, improving and growing — whether it’s a quick website change, a technical fix, or another round of SEO. One team who knows your business and gets it done."
      problem={{
        heading: 'Small jobs shouldn’t mean chasing freelancers for days',
        body: [
          'You need a price updated, a page changed, or something fixed — and you’re stuck waiting on a freelancer who takes days to reply, or explaining your whole setup from scratch to yet another stranger.',
          'So the little jobs pile up. Your site slowly falls out of date, you worry something will break, and you’re quietly juggling three different contractors who don’t talk to each other. It’s more hassle than it should be.',
        ],
      }}
      whatYouGetIntro="One reliable team who knows your whole setup and handles whatever your digital side needs — as little or as much as you like."
      whatYouGet={seo.whatWeDoItems}
      howItWorks={[
        { title: 'A quick chat', body: 'We talk through your setup and what kind of support you’re after — no long commitment required.' },
        { title: 'We learn your setup', body: 'We get to know your site, accounts and tools, so you never have to explain the basics twice.' },
        { title: 'You send requests', body: 'Need something done? Just tell us. Drop us a message whenever a job comes up.' },
        { title: 'We handle it', body: 'We make the changes, fix the issues and keep improving things quietly in the background.' },
      ]}
      proofHeading="A partnership that keeps delivering"
      proofSub="Eclipse Paving started with a single project and became a long-term client — we now look after their site, SEO, ads and more, all as one ongoing relationship."
      pricing={{
        price: '£50–£75 / hr',
        caption: 'Flexible support — pay for what you need, or set up a monthly retainer. No lock-in.',
      }}
      finalHeading="Want a digital partner in your corner?"
      finalSub="Book a call and we’ll talk through your setup and how ongoing support could take the digital hassle off your plate for good."
    />
  );
}
